import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionRequired } from '@/hooks/useSubscriptionRequired';
import { shouldBlockForSubscription } from '@/lib/subscription-rules';
import { recordRedirect } from '@/lib/subscription-metrics';
import { CareOSLoader } from '@/components/ui/careos-loader';

interface SubscriptionGuardProps {
  children: ReactNode;
}

/**
 * SubscriptionGuard - Protects routes based on subscription status.
 *
 * All decision logic lives in `shouldBlockForSubscription` (pure + tested).
 * The guard only gathers inputs. Bias: never redirect when in doubt.
 */
export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const {
    subscription,
    loading,
    isLoggingOut,
    hasCheckedSubscription,
    isCheckingSubscription,
    isRevalidatingSubscription,
    consecutiveNegativeChecks,
  } = useAuth();
  const {
    isRequired,
    isOwner,
    isLoading: isLoadingUsage,
    isFetching: isFetchingUsage,
  } = useSubscriptionRequired();

  // Block rendering only while auth itself is unresolved or during logout.
  if (loading || isLoggingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CareOSLoader size="lg" />
      </div>
    );
  }

  // First-load: wait for a definitive picture before showing the app shell.
  if (!hasCheckedSubscription || isLoadingUsage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CareOSLoader size="lg" />
      </div>
    );
  }

  const isResolving =
    isCheckingSubscription || isRevalidatingSubscription || isFetchingUsage;

  const shouldBlock = shouldBlockForSubscription({
    isOwner,
    isRequired,
    subscribed: subscription.subscribed,
    subscriptionEnd: subscription.subscriptionEnd,
    consecutiveNegativeChecks,
    isResolving,
  });

  if (shouldBlock) {
    console.warn('[SubscriptionGuard] Redirecting to /app/subscription', {
      isOwner,
      isRequired,
      subscribed: subscription.subscribed,
      subscriptionEnd: subscription.subscriptionEnd,
      consecutiveNegativeChecks,
    });
    recordRedirect('no-subscription', {
      is_owner: isOwner,
      is_required: isRequired,
      subscribed: subscription.subscribed,
      subscription_end: subscription.subscriptionEnd,
      negative_checks: consecutiveNegativeChecks,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    });
    return <Navigate to="/app/subscription" replace />;
  }

  return <>{children}</>;
}

